const { mainConfigService } = require('../services/config');
const {
  ccvValidation
} = require('../util');
const {
  tokenPayFetch,
  fetchEncKey
} = require('../http');
const { encryptCHD } = require('../cryptography');
const { PAYMENT_ERROR_MESSAGES } = require('./errorMessages');
const { SDK_FOPS } = require('../variables');

class TokenPaymentFlow {
  constructor(documentCheckout, callbackService) {
    this.documentCheckout = documentCheckout;
    this.callbackService = callbackService;
  }

  async processPayment(method) {
    const flowContext = this._determinePaymentFlow(method);
    if (!flowContext) {
      throw new Error('Invalid payment method configuration');
    }

    if (method.ccv_required) {
      return await this._processPaymentWithCCV(method, flowContext);
    } else {
      return await this._processPaymentWithoutCCV(method, flowContext);
    }
  }

  _determinePaymentFlow(method) {
    const paymentMethods = mainConfigService.get('payment_methods');
    
    // Return null if no payment methods are configured
    if (!paymentMethods || paymentMethods.length === 0) {
      return null;
    }
    
    const matchedMethod = paymentMethods.find(
      (pm) => pm.code === method.card_pg
    );
    const isOttuPg = matchedMethod?.flow === SDK_FOPS.OTTU_PG;

    if (matchedMethod && isOttuPg) {
      if (!matchedMethod.trandata) {
        console.error(PAYMENT_ERROR_MESSAGES.INVALID_PAYMENT_METHOD);
        throw new Error(PAYMENT_ERROR_MESSAGES.USER_GENERIC_ERROR);
      }
      return {
        isOttuPg: true,
        trandata: matchedMethod.trandata,
        encKeyUrl: matchedMethod.enc_key_url
      };
    }

    return {
      isOttuPg: false,
      trandata: null,
      encKeyUrl: null
    };
  }

  _extractCCVFromDOM() {
    let cardCCV = null;
    this.documentCheckout
      .querySelectorAll('.ottu__sdk-radio-div')
      .forEach((element) => {
        if (element.querySelector('input').checked) {
          cardCCV = element
            .querySelector('label')
            .querySelector('input').value;
        }
      });
    return cardCCV;
  }

  _validateCCV(cardCCV, method) {
    const validation = ccvValidation(cardCCV);
    if (validation.error) {
      const validationError = new Error(validation.errors['tokenCardCcvBlock']);
      validationError.type = 'validation';
      validationError.method = method;
      throw validationError;
    }
    return true;
  }


  _buildBaseParams(method) {
    return {
      submit_url: method.submit_url,
      apiKey: mainConfigService.get('apiKey'),
      merchant_id: mainConfigService.get('merchant_id'),
      session_id: mainConfigService.get('session_id'),
      code: method.code
    };
  }

  async _enhanceParamsForOttuPG(params, cardDetails, flowContext) {
    if (flowContext.isOttuPg) {
      try {
        const encKey = await fetchEncKey(
          mainConfigService.get('merchant_id'), 
          mainConfigService.get('apiKey'), 
          flowContext.encKeyUrl
        );
        
        if (!encKey || !encKey.public_key) {
          throw new Error(PAYMENT_ERROR_MESSAGES.ENCRYPTION_KEY_INVALID);
        }

        const encryptedData = await encryptCHD(
          encKey.public_key, 
          JSON.stringify(cardDetails), 
          'SHA-1'
        );
        
        if (!encryptedData) {
          throw new Error(PAYMENT_ERROR_MESSAGES.ENCRYPTION_FAILED);
        }
        
        params.trandata = flowContext.trandata;
        params.enc = encryptedData;
      } catch (error) {
        console.error(PAYMENT_ERROR_MESSAGES.ENCRYPTION_KEY_FETCH_FAILED, error);
        throw new Error(PAYMENT_ERROR_MESSAGES.PAYMENT_DATA_PROCESSING_ERROR);
      }
    }
    return params;
  }

  async _processPaymentWithCCV(method, flowContext) {
    const cardCCV = this._extractCCVFromDOM();
    
    this._validateCCV(cardCCV, method);

    const cardDetails = { token: method.code };
    if (flowContext.isOttuPg) {
      cardDetails.cvv = cardCCV;
    }

    const params = this._buildBaseParams(method);
    await this._enhanceParamsForOttuPG(params, cardDetails, flowContext);
    
    return await this._executeTokenPayment(params, flowContext.isOttuPg, cardCCV);
  }

  async _processPaymentWithoutCCV(method, flowContext) {
    const cardDetails = { token: method.code };
    const params = this._buildBaseParams(method);
    await this._enhanceParamsForOttuPG(params, cardDetails, flowContext);
    
    return await this._executeTokenPayment(params, flowContext.isOttuPg);
  }

  async _executeTokenPayment(params, isOttuPg, cardCCV = null) {
    const response = await tokenPayFetch(
      params.submit_url,
      params.apiKey,
      params.merchant_id,
      params.session_id,
      params.code,
      params.trandata || undefined,
      params.enc || undefined,
      isOttuPg,
      cardCCV
    );
    
    return response;
  }

}

module.exports = TokenPaymentFlow;